import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiService } from 'app/core/api/api.service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

export interface SupportTicket {
    _id: string;
    userId: string;
    email: string;
    comments: string;
    document: string | null;
    status: 'open' | 'closed' | 'in-progress';
    createdAt: string;
    updatedAt: string;
    __v?: number;
}

export interface SupportTicketsResponse {
    success: boolean;
    data: {
        tickets: SupportTicket[];
        pagination: {
            total: number;
            totalPages: number;
            currentPage: number;
            perPage: number;
        };
    };
}

export interface StatusUpdatePayload {
    status: 'open' | 'closed' | 'in-progress';
    [key: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class SupportService {
    private _apiService = inject(ApiService);
    
    // BehaviorSubjects for reactive state management
    private _tickets: BehaviorSubject<SupportTicket[]> = new BehaviorSubject<SupportTicket[]>([]);
    private _pagination: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    
    // Data store
    private dataStore: { tickets: SupportTicket[], pagination: any } = { 
        tickets: [], 
        pagination: null 
    };

    constructor(private _httpClient: HttpClient) {
        console.log('SupportService initialized');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for tickets observable
     */
    get tickets$(): Observable<SupportTicket[]> {
        return this._tickets.asObservable();
    }

    /**
     * Getter for pagination observable
     */
    get pagination$(): Observable<any> {
        return this._pagination.asObservable();
    }

    /**
     * Getter for stored tickets
     */
    getTickets(): SupportTicket[] {
        return this.dataStore.tickets;
    }

    /**
     * Getter for stored pagination
     */
    getPagination(): any {
        return this.dataStore.pagination;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all user support tickets
     */
    getUserTickets(): Observable<SupportTicketsResponse> {
        console.log('Fetching user tickets...');
        
        return this._apiService.get('support/user-tickets').pipe(
            tap((response: SupportTicketsResponse) => {
                console.log('Support tickets response:', response);
                
                if (response?.success) {
                    this.dataStore.tickets = response.data.tickets;
                    this.dataStore.pagination = response.data.pagination;
                    
                    this._tickets.next(response.data.tickets);
                    this._pagination.next(response.data.pagination);
                    
                    console.log(`Loaded ${response.data.tickets.length} tickets`);
                    console.log('Pagination:', response.data.pagination);
                } else {
                    console.warn('API returned success: false', response);
                }
            }),
            catchError((error) => {
                console.error('Error fetching support tickets:', error);
                console.error('Error details:', {
                    status: error?.status,
                    message: error?.message,
                    url: error?.url,
                    headers: error?.headers
                });
                return throwError(() => error);
            })
        );
    }

    /**
     * Get ticket by ID
     * @param id Ticket ID
     */
    getTicketById(id: string): Observable<any> {
        console.log(`Fetching ticket by ID: ${id}`);
        
        return this._apiService.get(`support/${id}`).pipe(
            tap((response: any) => {
                console.log('Ticket details response:', response);
            }),
            catchError((error) => {
                console.error(`Error fetching ticket ${id}:`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Update ticket status
     * @param id Ticket ID
     * @param payload Status update payload
     */
    updateTicketStatus(id: string, payload: StatusUpdatePayload): Observable<any> {
        console.log(`Updating ticket ${id} status to:`, payload.status);
        
        return this._apiService.patch(`support/${id}/status`, payload).pipe(
            tap((response: any) => {
                console.log('Status update response:', response);
                
                // Update local data if successful
                if (response?.success) {
                    const ticketIndex = this.dataStore.tickets.findIndex(ticket => ticket._id === id);
                    if (ticketIndex !== -1) {
                        this.dataStore.tickets[ticketIndex] = {
                            ...this.dataStore.tickets[ticketIndex],
                            ...response.data, // Assuming response contains updated ticket
                            status: payload.status
                        };
                        
                        this._tickets.next(this.dataStore.tickets);
                        console.log(`Ticket ${id} status updated locally`);
                    }
                }
            }),
            catchError((error) => {
                console.error(`Error updating ticket ${id} status:`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Refresh tickets data
     */
    refreshTickets(): void {
        this.getUserTickets().subscribe();
    }

    /**
     * Get ticket by ID from local store
     * @param id Ticket ID
     */
    getTicketFromStore(id: string): SupportTicket | undefined {
        return this.dataStore.tickets.find(ticket => ticket._id === id);
    }

    /**
     * Update ticket in local store (for optimistic updates)
     * @param updatedTicket Updated ticket object
     */
    updateTicketInStore(updatedTicket: SupportTicket): void {
        const ticketIndex = this.dataStore.tickets.findIndex(ticket => ticket._id === updatedTicket._id);
        if (ticketIndex !== -1) {
            this.dataStore.tickets[ticketIndex] = updatedTicket;
            this._tickets.next(this.dataStore.tickets);
        }
    }

    /**
     * Clear all tickets data
     */
    clearTickets(): void {
        this.dataStore.tickets = [];
        this.dataStore.pagination = null;
        this._tickets.next([]);
        this._pagination.next(null);
    }

    /**
     * Debug method to check current state
     */
    debug(): void {
        console.log('Current SupportService State:');
        console.log('- Tickets count:', this.dataStore.tickets.length);
        console.log('- Tickets:', this.dataStore.tickets);
        console.log('- Pagination:', this.dataStore.pagination);
    }
}